import React, { useCallback, useRef, useState } from 'react'
import {
  AppInputFile,
  AppInputFileColor,
} from '../../../components/AppInput/AppInputFile'
import {
  AppImagePopup,
  IAppImagePopup,
} from '../../../components/AppImagePopup/AppImagePopup'
import { AppLoader, LoaderType } from 'components/AppLoader'
import { useOutsideClick } from '../../../utils/useOutsideClick'
import { useTranslation } from 'react-i18next'
import imageCompression from 'browser-image-compression'
import './style.scss'
import heic2any from 'heic2any'
import { convertWebPToJpeg } from 'utils/convertWebPToJpeg'
import { blobToFile } from 'utils/blobToFile'
import { AppInputFileMultiple } from 'components/AppInput/AppInputFileMultiple'

export interface IDragAndDropUploadPhotosProps {
  fileURLs?: string[] | null
  onSetFiles: (files: File[]) => void
  setFilePreview?: (files: string[]) => void
  onReplaceFileAtIndex?: (file: File, index: number) => void
  setFileFromCamera?: (value: boolean) => void
  isLoading?: boolean
  setIsDirty?: (value: boolean) => void
  setIsDeletedFile?: (value: boolean) => void
  mt?: boolean
  disabled?: boolean
  extraText?: string
  shortMessage?: boolean
}

const options = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 800,
  useWebWorker: true,
}

export const DragAndDropUploadPhotos = ({
  fileURLs,
  onSetFiles,
  setFilePreview,
  onReplaceFileAtIndex,
  mt,
  setFileFromCamera,
  isLoading,
  setIsDirty,
  setIsDeletedFile,
  disabled,
  extraText,
  shortMessage,
}: IDragAndDropUploadPhotosProps) => {
  const { t } = useTranslation()
  const [drag, setDrag] = useState(false)
  const dragCounterRef = useRef(0)
  const dropZoneRef = useRef<HTMLDivElement | null>(null)
  const savedHeightRef = useRef<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingConversion, setLoadingConversion] = useState(false)
  const [showLoader, setShowLoader] = useState<boolean>(false)
  const [showLoaderConversion, setShowLoaderConversion] =
    useState<boolean>(false)
  const [imagePopup, setImagePopup] = useState<IAppImagePopup>({
    active: false,
    photo: null,
    title: null,
  })

  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const refMenu = useRef<HTMLDivElement | null>(null)
  useOutsideClick(refMenu, () => setActiveIndex(null), activeIndex !== null)

  const toggleMenu = (index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index))
  }

  const onReplaceFile = async (file: File | File[] | null, index: number) => {
    if (!file) return
    const fileArray = Array.isArray(file) ? file : [file]
    const processed = await processFile(fileArray[0])
    if (onReplaceFileAtIndex) {
      onReplaceFileAtIndex(processed, index)
    } else {
      const newPreviewUrl = URL.createObjectURL(processed)
      const newPreviews = [...(fileURLs || [])]
      newPreviews[index] = newPreviewUrl
      setFilePreview?.(newPreviews)
      onSetFiles([processed])
    }
    setIsDirty?.(true)
  }

  const handleLoading = (isLoading: boolean) => {
    if (isLoading) {
      setShowLoader(false)
      setTimeout(() => {
        setShowLoader(true)
      }, 700)
    } else {
      setShowLoader(false)
    }
    setLoading(isLoading)
  }

  const handleLoadingConversion = (isLoading: boolean) => {
    if (isLoading) {
      setShowLoaderConversion(false)
      setTimeout(() => {
        setShowLoaderConversion(true)
      }, 700)
    } else {
      setShowLoaderConversion(false)
    }
    setLoadingConversion(isLoading)
  }

  const processFile = async (file: File): Promise<File> => {
    let processedFile = file

    if (file.name.endsWith('.heic')) {
      handleLoadingConversion(true)
      try {
        const imageBlob = (await heic2any({
          blob: file,
          toType: 'image/jpeg',
        })) as Blob
        processedFile = blobToFile(
          imageBlob,
          file.name.replace('.heic', '.jpg'),
        )
      } catch (error) {
        console.error('Ошибка конвертации HEIC:', error)
      }
    }

    if (processedFile.type === 'image/webp') {
      handleLoadingConversion(true)
      processedFile = await convertWebPToJpeg(processedFile)
    }

    handleLoading(true)
    try {
      const compressedFile = await imageCompression(processedFile, options)
      return compressedFile
    } catch (error) {
      console.error('Ошибка сжатия:', error)
      return processedFile
    }
  }

  const onDropHandler = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const fileList = e.dataTransfer.files
    if (!fileList || fileList.length === 0) return

    const processedFiles: File[] = []

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i]
      const processed = await processFile(file)
      processedFiles.push(processed)
    }

    onSetFiles(processedFiles)
    setLoading(false)
    setLoadingConversion(false)
    dragCounterRef.current = 0
    if (dropZoneRef.current) {
      dropZoneRef.current.style.minHeight = ''
    }
    savedHeightRef.current = null
    setDrag(false)
    setIsDirty?.(true)
  }

  const onSetFileHandler = useCallback(
    async (file: File | File[] | null) => {
      if (!file) return

      const fileArray = Array.isArray(file) ? file : [file]
      const processedFiles: File[] = []

      for (const f of fileArray) {
        const processed = await processFile(f)
        processedFiles.push(processed)
      }

      onSetFiles(processedFiles)
      setLoading(false)
      setLoadingConversion(false)
      setIsDirty?.(true)
    },
    [onSetFiles, setIsDirty],
  )

  const removeFile = useCallback(
    (index: number) => {
      const newPreviews = fileURLs?.filter((_, i) => i !== index) || []
      setFilePreview?.(newPreviews)
      setIsDeletedFile?.(true)
      setIsDirty?.(true)
    },
    [fileURLs, setFilePreview, setIsDeletedFile, setIsDirty],
  )

  const closeImagePopupHandler = useCallback(() => {
    setImagePopup({ active: false, photo: null, title: null })
  }, [])

  const openImagePopupHandler = useCallback((photo: string, title: string) => {
    setImagePopup({ active: true, photo, title })
  }, [])

  const dragEnterHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    dragCounterRef.current += 1
    if (dragCounterRef.current === 1) {
      if (dropZoneRef.current) {
        savedHeightRef.current = `${dropZoneRef.current.offsetHeight}px`
        dropZoneRef.current.style.minHeight = savedHeightRef.current
      }
      setDrag(true)
    }
  }

  const dragLeaveHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    dragCounterRef.current -= 1
    if (dragCounterRef.current === 0) {
      if (dropZoneRef.current) {
        dropZoneRef.current.style.minHeight = ''
      }
      savedHeightRef.current = null
      setDrag(false)
    }
  }

  const dragOverHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const hasPhotos = fileURLs && fileURLs.length > 0

  return (
    <React.Fragment>
      {imagePopup.active && imagePopup.photo && (
        <AppImagePopup
          title={imagePopup.title || ''}
          img={imagePopup.photo}
          setActive={closeImagePopupHandler}
        />
      )}

      {hasPhotos && (
        <div className={`upload-photo-block with-photo ${mt ? 'mt-20' : ''}`}>
          {fileURLs!.map((url, index) => (
            <div key={index} className="photo-item">
              <div
                className="loupe-block"
                onClick={() => openImagePopupHandler(url, '')}
              >
                <i className="an-ico an-ico-search" />
              </div>
              <div className="photo-menu-ico" onClick={() => toggleMenu(index)}>
                <i className="an-ico an-ico-dotes-v" />
              </div>
              {activeIndex === index && !disabled && (
                <div className="photo-menu" ref={refMenu}>
                  <div className="photo-menu-item">
                    <i className="an-ico an-ico-reload" />
                    <AppInputFile
                      onChangeFile={(file) => onReplaceFile(file, index)}
                      setLoading={handleLoading}
                      setLoadingConversion={handleLoadingConversion}
                      text={t('Заменить фото')}
                      color={AppInputFileColor.gray}
                    />
                  </div>
                  <div
                    className="photo-menu-item"
                    onClick={() => removeFile(index)}
                  >
                    <i className="an-ico an-ico-basket" />
                    {t('Удалить')}
                  </div>
                </div>
              )}
              <img src={url} alt={`photo-${index}`} />
            </div>
          ))}
        </div>
      )}

      <div
        ref={dropZoneRef}
        className={`upload-photos-block ${drag && !disabled ? 'is-draggable' : 'flex-col'} ${mt ? 'mt-20' : ''}`}
        onDragEnter={dragEnterHandler}
        onDragLeave={dragLeaveHandler}
        onDragOver={dragOverHandler}
        onDrop={onDropHandler}
      >
        {drag && !disabled ? (
          <>
            {loadingConversion && showLoaderConversion ? (
              <span className="loading-text">
                {t('Конвертируем изображения...')}
              </span>
            ) : showLoader && loading ? (
              <span className="loading-text">
                {t('Сжимаем изображения...')}
              </span>
            ) : (
              <span>{t('Отпустите файлы, чтобы загрузить их')}</span>
            )}
          </>
        ) : (
          <>
            {showLoaderConversion && loadingConversion && (
              <span className="loading-text" style={{ marginBottom: '30px' }}>
                {t('Конвертируем изображения...')}
              </span>
            )}
            {showLoader && loading && (
              <span className="loading-text" style={{ marginBottom: '30px' }}>
                {t('Сжимаем изображения...')}
              </span>
            )}
            {isLoading && <AppLoader loaderType={LoaderType.table} />}
            {!disabled && (
              <div className="drag-block-wrap">
                <div className="link-wrap">
                  <i className="an-ico an-ico-clip" />
                  <div className="link-block">
                    <span>
                      {t('Перетащите')} <br /> {t('или')}{' '}
                      <AppInputFileMultiple
                        onChangeFile={onSetFileHandler}
                        setLoading={handleLoading}
                        setLoadingConversion={handleLoadingConversion}
                        extraText={extraText}
                        multiple
                      />
                    </span>
                    <br />
                    <div className="mt-10" />
                    {shortMessage ? (
                      <small>{t('Максимальный размер файла 30 Мб')}</small>
                    ) : (
                      <small>
                        {t(
                          'Максимальный размер файла 300 Мбайт. Рекомендуемый размер 800 на 600',
                        )}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </React.Fragment>
  )
}
